(() => {
  if (customElements.get('dj-cart-shipping')) return;
  const cache = new Map();
  let queue = Promise.resolve();
  const root = window.Shopify?.routes?.root || '/';
  const cartKey = cart => [
    cart.currency, cart.total_price, Math.round(cart.total_weight),
    cart.items.map(item => item.key).join(','),
    cart.items.map(item => item.quantity).join(',')
  ].join('|');
  const readCart = async () => {
    const response = await fetch(root + 'cart.js', {
      credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(15000)
    });
    if (!response.ok) throw new Error('cart_request_failed');
    return response.json();
  };
  const createQuery = `mutation ShippingPreview($input:CartInput!) {
    cartCreate(input:$input) { cart { id totalQuantity } userErrors { field message } }
  }`;
  const ratesQuery = `query ShippingPreviewRates($id:ID!) {
    cart(id:$id) { ... @defer {
      deliveryGroups(first:10,withCarrierRates:true) {
        nodes { deliveryOptions { title estimatedCost { amount currencyCode } } }
        pageInfo { hasNextPage }
      }
    } }
  }`;
  const storefront = async (query, variables) => {
    const response = await fetch('/api/2026-10/graphql.json', {
      method: 'POST', credentials: 'omit', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json, multipart/mixed' },
      body: JSON.stringify({ query, variables }), signal: AbortSignal.timeout(35000)
    });
    if (!response.ok) throw new Error('shipping_request_failed');
    const body = await response.text();
    const boundary = response.headers.get('content-type')?.match(/boundary="?([^";\s]+)/i)?.[1];
    const results = boundary
      ? body.split('--' + boundary).flatMap(part => {
        const separator = part.indexOf('\r\n\r\n');
        if (separator < 0) return [];
        const value = part.slice(separator + 4).trim();
        return value ? [JSON.parse(value)] : [];
      })
      : [JSON.parse(body)];
    if (!results.length || results.some(result => result.errors?.length || result.incremental?.some(patch => patch.errors?.length))) {
      throw new Error('shipping_response_failed');
    }
    return results;
  };
  const calculate = async (key, currency) => {
    const cart = await readCart();
    if (cartKey(cart) !== key) throw new Error('cart_changed');
    if (!cart.items.length || cart.items.length > 250) throw new Error('shipping_unavailable');
    const lines = cart.items.map(item => ({
      merchandiseId: 'gid://shopify/ProductVariant/' + item.variant_id,
      quantity: item.quantity,
      ...(item.selling_plan_allocation?.selling_plan?.id
        ? { sellingPlanId: 'gid://shopify/SellingPlan/' + item.selling_plan_allocation.selling_plan.id } : {})
    }));
    const created = await storefront(createQuery, { input: {
      lines, buyerIdentity: { countryCode: 'SE' },
      delivery: { addresses: [{ selected: true, oneTimeUse: true, address: {
        deliveryAddress: { address1: 'Testvägen 1', city: 'Tomelilla', countryCode: 'SE', zip: '27330' }
      } }] }
    } });
    const preview = created[0]?.data?.cartCreate;
    if (preview?.userErrors?.length || !preview?.cart?.id || preview.cart.totalQuantity !== cart.item_count) {
      throw new Error('shipping_unavailable');
    }
    const parts = await storefront(ratesQuery, { id: preview.cart.id });
    let groups;
    for (const part of parts) {
      groups = part.data?.cart?.deliveryGroups || groups;
      for (const patch of part.incremental || []) groups = patch.data?.deliveryGroups || groups;
    }
    if (!groups?.nodes?.length || groups.pageInfo?.hasNextPage) throw new Error('shipping_unavailable');
    let price = 0;
    let varied = false;
    for (const group of groups.nodes) {
      const prices = group.deliveryOptions
        .filter(option => option.estimatedCost?.currencyCode === currency)
        .map(option => Math.round(Number(option.estimatedCost.amount) * 100))
        .filter(amount => Number.isFinite(amount) && amount >= 0);
      if (!prices.length) throw new Error('shipping_unavailable');
      price += Math.min(...prices);
      varied = varied || new Set(prices).size > 1;
    }
    if (cartKey(await readCart()) !== key) throw new Error('cart_changed');
    return { price, varied };
  };
  const getQuote = (key, currency) => {
    const stored = cache.get(key);
    if (stored && Date.now() - stored.created < 60000) return stored.promise;
    const promise = queue.catch(() => {}).then(() => calculate(key, currency));
    queue = promise;
    cache.set(key, { promise, created: Date.now() });
    promise.catch(() => { if (cache.get(key)?.promise === promise) cache.delete(key); });
    return promise;
  };
  class DjCartShipping extends HTMLElement {
    connectedCallback() {
      if (this.dataset.state === 'overweight' || this.initialized) return;
      this.initialized = true;
      this.price = this.querySelector('[data-shipping-price]');
      this.total = this.querySelector('[data-shipping-total]');
      this.totalRow = this.querySelector('[data-shipping-total-row]');
      this.totalLabel = this.querySelector('[data-shipping-total-label]');
      this.note = this.querySelector('[data-shipping-note]');
      this.retry = this.querySelector('[data-shipping-retry]');
      this.retry.addEventListener('click', () => this.refresh());
      this.refresh();
    }
    async refresh() {
      if (this.loading || !this.isConnected) return;
      this.loading = true;
      this.dataset.state = 'loading';
      this.price.textContent = this.dataset.loading;
      this.totalRow.hidden = true;
      this.retry.hidden = true;
      this.note.textContent = this.dataset.note;
      try {
        const rate = await getQuote(this.dataset.cartKey, this.dataset.currency);
        if (!this.isConnected) return;
        const threshold = Number(this.dataset.freeShippingThreshold);
        const quote = threshold > 0 && Number(this.dataset.subtotal) >= threshold
          ? { price: 0, varied: false } : rate;
        const format = value => new Intl.NumberFormat(document.documentElement.lang || 'sv-SE', {
          style: 'currency', currency: this.dataset.currency,
          minimumFractionDigits: value % 100 ? 2 : 0, maximumFractionDigits: 2
        }).format(value / 100);
        const amount = quote.price === 0 ? this.dataset.free : format(quote.price);
        this.price.textContent = quote.varied ? this.dataset.from.replace('[price]', amount) : amount;
        this.total.textContent = format(Number(this.dataset.subtotal) + quote.price);
        this.totalLabel.textContent = quote.varied ? this.dataset.totalFromLabel : this.dataset.totalLabel;
        this.totalRow.hidden = false;
        this.dataset.state = 'ready';
      } catch (error) {
        if (!this.isConnected) return;
        this.price.textContent = '—';
        this.note.textContent = error.message === 'shipping_unavailable' ? this.dataset.unavailable : this.dataset.error;
        this.retry.hidden = false;
        this.dataset.state = 'error';
      } finally {
        this.loading = false;
      }
    }
  }
  customElements.define('dj-cart-shipping', DjCartShipping);
  window.addEventListener('themeCartOpened', () => {
    document.querySelectorAll('dj-cart-shipping').forEach(element => {
      if (element.dataset.state !== 'overweight') element.refresh();
    });
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      cache.clear();
      document.querySelectorAll('dj-cart-shipping').forEach(element => {
        if (element.dataset.state !== 'overweight') element.refresh();
      });
    }
  });
})();
